import {Box, Button, Modal, Typography} from '@viptech/react-components'
import {useSnackbar} from 'notistack'
import {useEffect, useState} from 'react'
import {Icon} from 'src/common/generics/components/icon/IconBase'
import {ValidateUserPermissions} from 'src/common/utils/ValidatePermissions'
import CloseIcon from 'src/images/closeBlueIcon.svg'
import api from 'src/services/api'
import {ListTypeDetectionColorEnum} from 'src/utilities/enums/listTypeDetectionColorEnum'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'
import Dropdown from '../../../../components/dropdown/Dropdown'
import {DropdownItem} from '../../../../components/dropdown/DropdownProps'
import {Input} from '../../../../components/input/Input'
import useClients from '../../hooks/useClients'
import {List} from '../../hooks/useLists'

type EditListModalProps = {
  isOpen: boolean
  closeModal: () => void
  list: List
  cancelCallback: () => void
}

function EditListModal(props: EditListModalProps) {
  const {isOpen, closeModal, list, cancelCallback} = props
  const [name, setName] = useState(list.name || '')
  const [listType, setListType] = useState(list.type === 'Faces' ? 'Facial' : 'Placas')
  const [clientSelected, setClientSelected] = useState({} as DropdownItem)
  const [isLoading, setIsLoading] = useState(false)
  const [listTypeDetection, setListTypeDetection] = useState<DropdownItem>(getListType(list.listColor))

  const {enqueueSnackbar} = useSnackbar()
  const {clients: clientItems, getClients} = useClients()
  const hasFacialUpdatePermission = ValidateUserPermissions('face:update')
  const hasLicensePlateUpdatePermission = ValidateUserPermissions('lpr:update')

  if ((listType === 'Facial' && !hasFacialUpdatePermission) || (listType === 'Placas' && !hasLicensePlateUpdatePermission)) handleClose()

  useEffect(() => {
    getClients(list.companyId)
    if (list.clientId && list.client !== 'Lista compartilhada') setClientSelected({id: `${list.clientId}`, label: list.client})
  }, [list])

  function clearFields() {
    setName('')
    setListType('')
    setClientSelected({} as DropdownItem)
  }

  function handleClose() {
    clearFields()
    closeModal()
  }

  const listTypeDetectionItems = [
    {id: 'blacklist', label: 'Bloqueado'},
    {id: 'whitelist', label: 'Permitido'},
  ]

  function getListType(listColor?: string) {
    if (listColor === ListTypeDetectionColorEnum.BLACKLIST) return {id: 'blacklist', label: 'Bloqueado'}
    if (listColor === ListTypeDetectionColorEnum.WHITELIST) return {id: 'whitelist', label: 'Permitido'}
    return {} as DropdownItem
  }

  const findColorByDetectionType = (detectionTypeId: string) => {
    if (detectionTypeId === 'blacklist') return ListTypeDetectionColorEnum.BLACKLIST
    else if (detectionTypeId === 'whitelist') return ListTypeDetectionColorEnum.WHITELIST
    return undefined
  }

  async function handleEditList() {
    try {
      setIsLoading(true)
      const body = {
        name: name,
        clientId: Number(clientSelected.id),
        listColor: findColorByDetectionType(listTypeDetection.id),
      }
      if (listType === 'Facial') {
        await api.facialRecognition.updateFacialItemList(list.id, body)
      } else {
        await api.lpr.updateLprList(list.id, body)
      }
      enqueueSnackbar('Lista editada com sucesso', {variant: 'success'})
      handleClose()
    } catch (err) {
      handleErrorWithSnackbar(enqueueSnackbar, err, 'Erro ao editar lista')
    } finally {
      setIsLoading(false)
    }
  }

  function disableSaveButton() {
    if (name !== list.name) return false
    if (+clientSelected.id !== list.clientId && clientSelected.id) return false
    if (listTypeDetection.id) return false
    return true
  }

  return (
    <Modal isOpen={isOpen} closeModal={() => cancelCallback()} backgroundColor='white'>
      <Box padding='20px' display='flex' direction='column' justifyContent='space-between' height='360px' width='400px'>
        <Box display='flex' justifyContent='space-between'>
          <Typography variant='p' weight={500} size='18px' color='#344054'>
            Editar Lista
          </Typography>

          <Icon src={CloseIcon} cursor='pointer' onClick={() => cancelCallback()} style={{width: '12px'}} />
        </Box>

        <Box height='80%' display='flex' direction='column' justifyContent='space-between'>
          <Box display='flex' direction='column' rowGap='40px'>
            <Input.Root
              labelFontSize='14px'
              labelFontWeight='400'
              height='40px'
              borderColor='#D0D5DD'
              fontSize='12px'
              min={4}
              controlled
              value={name}
              label='Nome da lista'
              onChange={(e) => setName(e.target.value)}
              maxLength={99}
              required
            />

            <Dropdown
              labelFontSize='14px'
              height='40px'
              borderColor='#D0D5DD'
              fontSize='12px'
              items={listTypeDetectionItems}
              selected={listTypeDetection}
              label='Permissões da lista'
              onChangeSelected={(e) => setListTypeDetection(e)}
              required
            />

            <Dropdown
              labelFontSize='14px'
              height='40px'
              borderColor='#D0D5DD'
              fontSize='12px'
              selected={clientSelected}
              items={clientItems || []}
              disabled={!clientSelected.label}
              label='Cliente'
              onChangeSelected={(e) => setClientSelected(e)}
            />
          </Box>

          <Box display='flex' columnGap='10px' justify='flex-end'>
            <Button fontSize='12px' height='38px' width='120px' variant='outlined' color='#8E8E8E' onClick={() => cancelCallback()}>
              Cancelar
            </Button>
            <Button
              fontSize='12px'
              height='38px'
              width='120px'
              disabledTextColor='#8E8E8E'
              disabled={disableSaveButton()}
              isLoading={isLoading}
              onClick={() => !isLoading && handleEditList()}>
              Salvar
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default EditListModal
